import { Link } from "react-router-dom";
import { ArrowLeft, Linkedin, Facebook, Instagram } from "lucide-react";
import { scrollToSection } from "@/App";

export const P = ({ children, className = "" }) => (
  <p className={`text-base leading-loose text-[#2F3338]/85 ${className}`}>{children}</p>
);

export const Emphasis = ({ children }) => (
  <p className="border-l-2 border-[#B46A45]/60 py-1 pl-6 font-serif text-xl leading-relaxed text-[#3F5A47] sm:text-2xl">
    {children}
  </p>
);

export const Section = ({ title, subline, id, children }) => (
  <section id={id} className="mt-16 scroll-mt-28 first:mt-0">
    <h2 className="font-serif text-2xl text-[#3F5A47] sm:text-3xl">{title}</h2>
    {subline && <p className="mt-2 font-serif text-lg italic text-[#5B7F7E]">{subline}</p>}
    <div className="mt-6 space-y-6">{children}</div>
  </section>
);

const SOCIAL_ICONS = { linkedin: Linkedin, facebook: Facebook, instagram: Instagram };

const SocialLinks = ({ socials, name }) => (
  <div className="mt-3 flex items-center gap-4">
    {socials.map((s) => {
      const Icon = SOCIAL_ICONS[s.type];
      return (
        <a
          key={s.type}
          href={s.url}
          target="_blank"
          rel="noreferrer"
          aria-label={`${name} på ${s.type}`}
          data-testid={`social-link-${name.toLowerCase().split(" ")[0]}-${s.type}`}
          className="text-[#5B7F7E] transition-colors duration-300 hover:text-[#B46A45]"
        >
          <Icon className="h-5 w-5" strokeWidth={1.6} />
        </a>
      );
    })}
  </div>
);

export const Sub = ({ title, subline, image, socials, children }) => (
  <div className="border-t border-[#D6C2A6]/60 pt-10 first:border-t-0 first:pt-0">
    {image ? (
      <div className="flex flex-col gap-7 sm:flex-row sm:items-center">
        <img
          src={image}
          alt={title}
          className="h-48 w-40 shrink-0 rounded-[4px] object-cover"
        />
        <div>
          <h3 className="font-serif text-xl text-[#3F5A47] sm:text-2xl">{title}</h3>
          {subline && (
            <p className="mt-1.5 font-serif text-base italic text-[#5B7F7E]">{subline}</p>
          )}
          {socials && <SocialLinks socials={socials} name={title} />}
        </div>
      </div>
    ) : (
      <>
        <h3 className="font-serif text-xl text-[#3F5A47] sm:text-2xl">{title}</h3>
        {subline && <p className="mt-1.5 font-serif text-base italic text-[#5B7F7E]">{subline}</p>}
        {socials && <SocialLinks socials={socials} name={title} />}
      </>
    )}
    <div className="mt-5 space-y-5">{children}</div>
  </div>
);

export const Bullets = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item) => (
      <li key={item} className="flex gap-4 text-base leading-relaxed text-[#2F3338]/85">
        <span className="mt-[0.65em] h-1.5 w-1.5 shrink-0 rounded-full bg-[#B46A45]" />
        {item}
      </li>
    ))}
  </ul>
);

export const Example = ({ title, subline, testId, children }) => (
  <article
    data-testid={testId}
    className="rounded-[4px] border border-[#D6C2A6]/50 bg-[#D6C2A6]/15 p-8 sm:p-10"
  >
    <h3 className="font-serif text-xl text-[#3F5A47] sm:text-2xl">{title}</h3>
    {subline && <p className="mt-1.5 font-serif text-base italic text-[#5B7F7E]">{subline}</p>}
    <div className="mt-5 space-y-5">{children}</div>
  </article>
);

export const ContactCta = ({ title, children }) => (
  <section className="mt-20 border-t border-[#D6C2A6]/70 pt-14">
    <h2 className="font-serif text-2xl text-[#3F5A47] sm:text-3xl">{title}</h2>
    <div className="mt-6 space-y-6">{children}</div>
    <button
      onClick={() => scrollToSection("#kontakt")}
      data-testid="service-cta-kontakt"
      className="mt-9 rounded-full bg-[#3F5A47] px-8 py-3 text-sm font-medium text-[#FAF7F1] transition-colors duration-300 hover:bg-[#2F3338]"
    >
      Kontakt os
    </button>
  </section>
);

export const BackLink = () => (
  <p className="mt-16">
    <Link
      to="/vi-tilbyder"
      data-testid="service-back-link"
      className="inline-flex items-center gap-2 text-sm font-medium text-[#3F5A47] transition-colors hover:text-[#B46A45]"
    >
      <ArrowLeft className="h-4 w-4" /> Tilbage til Vi tilbyder
    </Link>
  </p>
);
